import React from "react";
import { formatExactDate, formatRelativeTime } from "../Helper/dates";

export type DeploymentState = "up-to-date" | "needs-deploy" | "pending";

export interface DeploymentStatusCard {
	label: string;
	status: DeploymentState;
	deployedAt?: string | null;
	action?: React.ReactNode;
}

interface DeploymentStatusPanelProps {
	cards: DeploymentStatusCard[];
	className?: string;
}

const stateStyles: Record<DeploymentState, { badge: string; indicator: string; statusLabel: string }> = {
	"up-to-date": {
		badge: "border-emerald-100 bg-emerald-50 text-emerald-700",
		indicator: "bg-emerald-500",
		statusLabel: "Up to date"
	},
	"needs-deploy": {
		badge: "border-amber-100 bg-amber-50 text-amber-700",
		indicator: "bg-amber-500",
		statusLabel: "Out of sync"
	},
	pending: {
		badge: "border-slate-100 bg-slate-50 text-slate-600",
		indicator: "bg-slate-400",
		statusLabel: "Pending deploy"
	}
};

/** Deployment status panel (preview/production) displayed as colored cards. */
const DeploymentStatusPanel: React.FC<DeploymentStatusPanelProps> = ({ cards, className }) => {
	return (
		<section className={`grid gap-4 md:grid-cols-2 ${className ?? ""}`}>
			{cards.map((card) => {
				const styles = stateStyles[card.status];
				const relative = card.deployedAt ? formatRelativeTime(card.deployedAt) : "Not yet deployed";
				const exact = card.deployedAt ? formatExactDate(card.deployedAt) : undefined;

				return (
					<div
						key={card.label}
						className={`rounded-2xl border bg-white p-5 text-slate-900 shadow-sm transition ${styles.badge}`}
					>
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-3">
								<span className={`inline-flex h-2 w-2 rounded-full ${styles.indicator}`} />
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-700">{card.label}</p>
							</div>
							{card.action && <div className="shrink-0">{card.action}</div>}
						</div>
						<p className="mt-3 text-2xl font-bold text-slate-900">{relative}</p>
						{exact && <p className="text-[11px] uppercase tracking-wide text-slate-500">{exact}</p>}
						<p className="mt-4 text-xs uppercase tracking-wide text-slate-500">
							Status: <span className="font-semibold text-slate-800">{styles.statusLabel}</span>
						</p>
					</div>
				);
			})}
		</section>
	);
};

export default DeploymentStatusPanel;

